import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type NotificationDownloadAction =
  | {
      kind: "top_folder_zip_download";
      jobId: string;
      itemKey: string;
      label: string;
    }
  | {
      kind: "pdf_split_view";
      jobId: string;
      label: string;
    };

export type NotificationItem = {
  id: string;
  message: string;
  severity: "success" | "error" | "warning" | "info";
  variant: "default" | "loading";
  autoHideMs: number | null;
  downloadAction?: NotificationDownloadAction;
};

function genNotificationId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export type PushNotificationPayload = {
  message: string;
  severity: NotificationItem["severity"];
  variant?: NotificationItem["variant"];
  autoHideMs?: number | null;
  id?: string;
  downloadAction?: NotificationDownloadAction;
};

interface NotificationState {
  items: NotificationItem[];
}

const initialState: NotificationState = {
  items: [],
};

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    showSessionExpired: (state) => {
      // Fixed id: a burst of 401s used to stack a column of identical sticky
      // toasts, one per failed request.
      const id = "session-expired";
      if (state.items.some((item) => item.id === id)) return;
      state.items.push({
        id,
        message: "Session expired. Please log in again.",
        severity: "warning",
        variant: "default",
        autoHideMs: null,
      });
    },
    pushNotification: (
      state,
      action: PayloadAction<PushNotificationPayload>,
    ) => {
      const p = action.payload;
      const id = p.id ?? genNotificationId();
      const variant = p.variant ?? "default";
      let autoHideMs: number | null;
      if (p.autoHideMs !== undefined) {
        autoHideMs = p.autoHideMs;
      } else if (variant === "loading") {
        autoHideMs = null;
      } else if (p.downloadAction) {
        autoHideMs = null;
      } else {
        autoHideMs = 6000;
      }
      const idx = state.items.findIndex((i) => i.id === id);
      const newItem: NotificationItem = {
        id,
        message: p.message,
        severity: p.severity,
        variant,
        autoHideMs,
        ...(p.downloadAction ? { downloadAction: p.downloadAction } : {}),
      };
      if (idx >= 0) {
        state.items[idx] = newItem;
      } else {
        state.items.push(newItem);
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((i) => i.id !== action.payload);
    },
    clearNotification: (state) => {
      state.items = [];
    },
  },
});

export const {
  showSessionExpired,
  pushNotification,
  removeNotification,
  clearNotification,
} = notificationSlice.actions;
export default notificationSlice.reducer;
